import { useMutation, useQueryClient } from '@tanstack/react-query'
import { COLLECTION_KEYS } from '@/hooks/collections/collectionKeys'
import { invalidateGroup } from '@/hooks/invalidation'
import { createListHooks } from '@/hooks/lists/createListHooks'
import { useDefinedQuery, type QueryDef } from '@/hooks/useDefinedQuery'
import { withIdempotency } from '@/hooks/withIdempotency'
import { api } from '@/services/api'
import {
  addToSelllist,
  removeFromSelllist,
  bulkRemoveFromSelllist,
  getSharedSelllist,
  markSelllistItemSold,
} from '@/services/api/selllist'
import { SessionChangedError } from '@/services/api/SessionChangedError'
import type { SharedSelllistResponse } from '@/types'

const KEYS = {
  list: () => ['selllist'] as const,
  quantities: () => ['selllistQuantities'] as const,
  shareToken: () => ['selllistShareToken'] as const,
  shared: (token: string) => ['sharedSelllist', token] as const,
}

const MUTATION_KEYS = {
  add: ['mutation', 'selllist', 'add'] as const,
  remove: ['mutation', 'selllist', 'remove'] as const,
  markSold: ['mutation', 'selllist', 'markSold'] as const,
}

// All selllist writes share one replay scope so queued offline changes replay
// in order. Settled keys mirror INVALIDATION_GROUPS.selllist — used by the
// runtime hooks AND the replay defaults in main.tsx (a replayed-after-reload
// mutation has only these, no hook callbacks).
const SELLLIST_SCOPE = { id: 'selllist' } as const
const SELLLIST_SETTLED_KEYS = [['selllist'], ['userQuantities']] as const

/**
 * @public Never imported by name: consumed at runtime by `lib/offlineMutations.ts`
 * (import.meta.glob over hooks/**) to register offline-replay defaults in
 * main.tsx. Un-exporting this silently breaks replay.
 */
export const selllistMutations = {
  add: {
    mutationKey: MUTATION_KEYS.add,
    mutationFn: ({
      cardId,
      quantity,
      idempotencyKey,
    }: {
      cardId: number
      quantity?: number
      idempotencyKey?: string
    }) => addToSelllist(cardId, quantity, idempotencyKey),
    offlineReplay: true,
    scope: SELLLIST_SCOPE,
    settledInvalidation: SELLLIST_SETTLED_KEYS,
  },
  remove: {
    mutationKey: MUTATION_KEYS.remove,
    mutationFn: ({ cardId, all, idempotencyKey }: { cardId: number; all?: boolean; idempotencyKey?: string }) =>
      removeFromSelllist(cardId, idempotencyKey, all || undefined),
    offlineReplay: true,
    scope: SELLLIST_SCOPE,
    settledInvalidation: SELLLIST_SETTLED_KEYS,
  },
  markSold: {
    mutationKey: MUTATION_KEYS.markSold,
    mutationFn: ({ cardId, idempotencyKey }: { cardId: number; idempotencyKey?: string }) =>
      markSelllistItemSold(cardId, idempotencyKey),
    offlineReplay: true,
    scope: SELLLIST_SCOPE,
    settledInvalidation: [
      ['selllist'],
      ['userQuantities'],
      ['collectionItemByCard'],
      ['collectionQuantitiesByBase'],
      ['dashboardStats'],
    ] as const,
  },
}

// Exported for useUserQuantities.ts cache seeding
export { KEYS as SELLLIST_KEYS }

const hooks = createListHooks({
  keys: {
    list: KEYS.list,
    quantities: KEYS.quantities,
    shareToken: KEYS.shareToken,
  },
  mutationKeys: MUTATION_KEYS,
  api: {
    getList: api.getSelllist,
    addItem: api.addToSelllist,
    removeItem: api.removeFromSelllist,
    getShareToken: api.getSelllistShareToken,
    regenerateShareToken: api.regenerateSelllistShareToken,
  },
  group: 'selllist',
})

export const useSelllist = hooks.useList
export const useSelllistQuantities = hooks.useQuantities
export const useAddToSelllist = hooks.useAdd
export const useRemoveFromSelllist = hooks.useRemove
export const useSelllistShareToken = hooks.useShareToken
export const useRegenerateSelllistToken = hooks.useRegenerateToken

export function useBulkRemoveFromSelllist() {
  const queryClient = useQueryClient()
  type Vars = { cardIds: number[]; idempotencyKey?: string }
  const mutation = useMutation({
    mutationFn: ({ cardIds, idempotencyKey }: Vars) => bulkRemoveFromSelllist(cardIds, idempotencyKey),
    onSuccess: () => invalidateGroup(queryClient, 'selllist'),
  })
  type PublicVars = number[] | Vars
  const normalize = (vars: PublicVars): Vars => (Array.isArray(vars) ? { cardIds: vars } : vars)
  return withIdempotency(mutation, normalize)
}

export function useMarkSelllistItemSold() {
  const queryClient = useQueryClient()
  type Vars = { cardId: number; idempotencyKey?: string }
  const mutation = useMutation({
    mutationKey: MUTATION_KEYS.markSold,
    mutationFn: ({ cardId, idempotencyKey }: Vars) => markSelllistItemSold(cardId, idempotencyKey),
    onMutate: async (vars: Vars) => {
      const selllistQKey = KEYS.quantities()
      const collQKey = COLLECTION_KEYS.quantities()
      await queryClient.cancelQueries({ queryKey: selllistQKey })
      await queryClient.cancelQueries({ queryKey: collQKey })
      const prevSelllist = queryClient.getQueryData<Record<number, number>>(selllistQKey)
      const prevCollection = queryClient.getQueryData<Record<number, number>>(collQKey)
      // Optimistic: remove from selllist quantities
      if (prevSelllist) {
        const next = { ...prevSelllist }
        delete next[vars.cardId]
        queryClient.setQueryData(selllistQKey, next)
      }
      // Optimistic: decrement from collection quantities (floor at 0 / delete key)
      if (prevCollection) {
        const sellQty = prevSelllist?.[vars.cardId] ?? 1
        const current = prevCollection[vars.cardId] ?? 0
        const next = { ...prevCollection }
        if (current - sellQty <= 0) {
          delete next[vars.cardId]
        } else {
          next[vars.cardId] = current - sellQty
        }
        queryClient.setQueryData(collQKey, next)
      }
      return { prevSelllist, prevCollection }
    },
    onError: (err, _vars, ctx) => {
      // Signed out meanwhile: the snapshot belongs to the previous session.
      if (err instanceof SessionChangedError) return
      if (ctx?.prevSelllist !== undefined) queryClient.setQueryData(KEYS.quantities(), ctx.prevSelllist)
      if (ctx?.prevCollection !== undefined) queryClient.setQueryData(COLLECTION_KEYS.quantities(), ctx.prevCollection)
    },
    onSettled: () => {
      // Invalidate everything the settled mutation touches (also used by replay)
      selllistMutations.markSold.settledInvalidation.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: key })
      })
    },
  })
  return withIdempotency(mutation)
}

// Shared selllist (public, no auth) — moved from useSharedLists.ts
const sharedSelllistQuery: QueryDef<SharedSelllistResponse, [string]> = {
  queryKey: KEYS.shared,
  queryFn: getSharedSelllist,
  auth: 'public',
}
export function useSharedSelllist(shareToken: string | undefined) {
  return useDefinedQuery(sharedSelllistQuery, shareToken as string, { enabled: !!shareToken })
}
